import { faArrowsUpDown, faCloudSlash, faQuestion, faSwap, faWifi, faWifiFair, faWifiWeak } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Label, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@playcastdotio/Shadcn';
import { useMemo } from 'react';

const pingConfig = [
  {
    name: 'good',
    range: [0, 30],
    color: 'text-emerald-400',
    icon: faWifi,
  },
  {
    name: 'fair',
    range: [31, 60],
    color: 'text-amber-400',
    icon: faWifiFair,
  },
  {
    name: 'weak',
    range: [61, 100],
    color: 'text-red-400',
    icon: faWifiWeak,
  },
];

const typeConfig = [
  {
    name: 'P2P',
    icon: faArrowsUpDown,
    color: 'text-emerald-400',
    tooltip: 'Your connection is Peer to Peer. This is the best type of connection',
  },
  {
    name: 'Relay',
    icon: faSwap,
    color: 'text-amber-400',
    tooltip: 'Your connection is through a relay server. This may cause instability issues',
  },
  {
    name: 'Local',
    icon: faCloudSlash,
    color: 'text-red-400',
    tooltip: 'Your connection is local. This player is on the same local network as you',
  },
  {
    name: 'Unknown',
    icon: faQuestion,
    color: 'text-gray-400',
    tooltip: 'Your connection type is unknown',
  },
];

const Ping = ({ ping }: { ping: number }) => {
  const config = useMemo(() => {
    return pingConfig.find((x) => ping >= x.range[0] && ping <= x.range[1]) || pingConfig[2];
  }, [ping]);

  return (
    <div className='flex items-center space-x-2 lg:space-x-1'>
      <FontAwesomeIcon icon={config.icon} className={`size-4 ${config.color}`} />
      <Label className='text-md flex-1 tabular-nums'>{ping.toFixed(0)}ms</Label>
    </div>
  );
};

export type ConnectionType = 'P2P' | 'Relay' | 'Local' | 'Unknown';

const Type = ({ type }: { type: ConnectionType }) => {
  const config = useMemo(() => {
    return typeConfig.find((x) => x.name === type);
  }, [type]);

  if (!config) {
    return null;
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger>
          <div className='flex cursor-pointer items-center space-x-2 lg:space-x-1'>
            <FontAwesomeIcon icon={config.icon} className={`size-4 ${config.color}`} />
            <Label className='text-md flex-1 cursor-pointer tabular-nums'>{config.name}</Label>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{config.tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const ConnectionV2 = ({ ping, type }: { ping: number; type: ConnectionType }) => {
  return (
    <div className='flex items-center gap-x-4'>
      <Ping ping={ping} />
      <Type type={type} />
    </div>
  );
};
